import { useMemo, useState, useRef } from 'react';
import {
  Box,
  Card,
  Container,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Tables from '../../../components/Tables';
import PageTitleWrapper from '../../../components/PageTitleWrapper';
import PageHeader from '../../../components/pageHeader';
import NoDataFound from '../../../components/NoDataFound';
import CommonDialog from '../../../utils/commenModel';
import { PageIcons } from 'src/assets/icons';
import AddStaff, { StaffFormValues } from './components/addstaff';
import { useTranslation } from 'react-i18next';
import { render } from 'react-dom';
import PageButton from 'src/components/PageButton';
import AppChip from 'src/components/pageStatusLable/AppChip';
import { useDeleteAdminStaffMutation, useGetAdminStaffListQuery, useUpdateAdminStaffStatusMutation } from 'src/reduxServer/services/adminStaff';
import Loader from 'src/components/Loader';
import { showToast } from 'src/utils/toastfy';
import { useNavigate } from 'react-router-dom';
import FormInputPage from 'src/components/FormFields/PageInput';
import { useDebounce } from 'src/utils/debounce';
import { Helmet } from 'react-helmet-async';

const AdminStaff = () => {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [openDelete, setOpenDelete] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [selectedRow, setSelectedRow] = useState<StaffFormValues | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState('')
  const { t } = useTranslation();
  const statusLawyerRef = useRef<any>(null);
  const [statusOpen, setStatusOpen] = useState(false)
  const navigate = useNavigate()


  const debouncedSearch = useDebounce(searchData, 500);

  const { data: staffListData, isLoading } = useGetAdminStaffListQuery(
    {
      search: debouncedSearch
    })
  const [deleteStaffData] = useDeleteAdminStaffMutation()
  const [updateStatus] = useUpdateAdminStaffStatusMutation();

  const consultationsData = useMemo(() => {
    if (!staffListData?.data) return [];

    return staffListData.data.map((item) => ({
      id: item.id,
      first_name: item.first_name,
      last_name: item.last_name,
      full_name: `${item.first_name} ${item.last_name}`,
      email: item.email,
      mobile: item.mobile,
      gender: item.gender,
      ID_type: item.ID_type,
      ID_num: item?.ID_num,
      is_active: item.is_active,
      dob: item?.dob

    }));
  }, [staffListData?.data]);

  const paginatedData = useMemo(() => {
    const start = page * limit;
    const end = start + limit;
    return consultationsData.slice(start, end);
  }, [consultationsData, page, limit]);

  /* -------------------- HANDLERS -------------------- */

  const handleNaviget = (row: any) => {
    navigate(`/Page/staff-datail/${row.id}`);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await deleteStaffData(selectedRow.id).unwrap();
      showToast("success", t('admin_staff_deleted_successfully'));
      setLoading(false);

    } catch (error: any) {
      showToast("error", error?.data?.msg || "Delete failed");
      setLoading(false);

    } finally {
      setOpenDelete(false);
    }
  };


  const handleStatusClick = async (row: any) => {
    statusLawyerRef.current = row
    setStatusOpen(true)

  };

  const onUpdateStatus = async () => {
    setLoading(true)
    try {
       await updateStatus({
        id: statusLawyerRef.current.id,
        is_active: !statusLawyerRef.current.is_active

      }).unwrap();

      showToast('success',
         t('Admin staff updated successfully.')
      );
      setStatusOpen(false)
      setLoading(false)


    } catch (error) {
      console.error("Failed to update status", error);
    }
  }

  /* -------------------- TABLE COLUMNS -------------------- */
  const columns = [
    {
      key: 'full_name',
      label: t('Name'),
      render: (row: any) => (
        <span
          onClick={() => handleNaviget(row)}
          style={{
            color: "#1976d2",
            cursor: "pointer",
          }}
        >
          {row.full_name}
        </span>
      )
    },
    {
      key: 'email',
      label: t('Email')
    },
    {
      key: 'mobile',
      label: t('Mobile')
    },
    {
      key: 'is_active',
      label: t('Status'),
      render: (row: any) => (
        <PageButton variant="text" onClick={() => handleStatusClick(row)} >
          <AppChip
            label={row.is_active ? t('Active') : t('Inactive')}
            variantType={row.is_active ? 'success' : 'error'}
          />
        </PageButton>
      )
    },
    {
      key: 'action',
      label: t('Actions'),
      render: (row: any) => (
        <>
          <Tooltip title={t('Edit')}>
            <IconButton
              onClick={() => {
                setSelectedRow(row);
                setOpenAdd(true);
              }}
              color="primary"
            >
              <PageIcons.Edit />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('Delete')}>
            <IconButton
              color="error"
              onClick={() => {
                setSelectedRow(row);
                setOpenDelete(true);
              }}
            >
              <PageIcons.Delete />
            </IconButton>
          </Tooltip>
        </>
      )
    }
  ];

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
       <Helmet>
                    <title>Admin Staff</title>
                </Helmet>
      <PageTitleWrapper>
        <PageHeader
          title={t('Admin Staff')}
          buttonLabel={t('addNew')}
          startIcon={<PageIcons.Add />}
          onButtonClick={() => {
            setSelectedRow(null);
            setOpenAdd(true);
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <FormInputPage
              label={t('Search')}
              value={searchData}
              onChange={(value) => setSearchData(value)}
            />
          </Stack>
        </PageHeader>

      </PageTitleWrapper>

      <Container maxWidth="xl">
        <Card sx={{ minHeight: 500 }}>
          {consultationsData.length === 0 ? (
            <Box display="flex" justifyContent="center" p={5}>
              <NoDataFound />
            </Box>
          ) : (
            <Tables
              columns={columns}
              data={paginatedData}
              total={consultationsData?.length}
              page={page}
              limit={limit}
              onPageChange={(_, newPage) => setPage(newPage)}
              onLimitChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(0);
              }}
            />

          )}
        </Card>
      </Container>

      {/* DELETE DIALOG */}
      <CommonDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        title={t('deleteConform')}
        content={`${selectedRow?.first_name} ${selectedRow?.last_name}`}
        onConfirm={confirmDelete}
        confirmText={t('Confirm')}
        cancelText={t('Cancel')}
        loading={loading}
      />

      <CommonDialog
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        title={t('update_status')}
        content={""}
        onConfirm={onUpdateStatus}
        confirmText={t('Confirm')}
        cancelText={t('Cancel')}
        loading={loading}
      />

      {/* ADD / EDIT DIALOG */}
      <AddStaff
        open={openAdd}
        onCancel={() => setOpenAdd(false)}
        updateData={selectedRow}
      />
    </>
  );
};

export default AdminStaff;
